import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'

import { formatApiErrorMessage } from '../api/apiErrorMessage'
import { ApiError } from '../api/client'
import { loadInventoryConnection } from '../api/inventoryConnection'
import {
  loadPurchasePlan,
  USAGE_WINDOW_DAYS,
  type PurchaseLineDto,
  type PurchaseOfferDto,
  type PurchasePlanDto,
} from '../api/inventoryPurchase'
import type { RestaurantCatalogOfferDto } from '../api/restaurantCatalog'
import { useAuth } from '../auth/AuthProvider'
import { useCart } from '../cart/CartProvider'
import { MarketPageHeader } from '../components/MarketPageHeader'
import { SelectCombobox } from '../components/SelectCombobox'
import { IconChevronDown } from '../components/icons/TableColumnIcons'
import { useActiveVenue } from '../venues/ActiveVenueProvider'
import { isDemoStandEnabled } from '../dev/demoStand'
import { InventoryDemoNotice } from '../dev/InventoryDemoNotice'

const formatQty = (value: number, unit: string) =>
  `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 3 }).format(value)} ${unit}`

const formatMoney = (value: number) =>
  new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 2,
  }).format(value)

export const dayCountLabel = (days: number) => {
  const lastTwo = days % 100
  const last = days % 10
  if (lastTwo >= 11 && lastTwo <= 14) return `${days} дней`
  if (last === 1) return `${days} день`
  if (last >= 2 && last <= 4) return `${days} дня`
  return `${days} дней`
}

const formatDate = (isoDate: string) =>
  new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' }).format(
    new Date(`${isoDate}T12:00:00`),
  )

export const stalePurchaseMessage = (plan: PurchasePlanDto) => {
  if (!plan.stale) {
    return null
  }
  if (plan.snapshot_date == null) {
    return `Остатки ещё не синхронизированы. Выполните синхронизацию за ${formatDate(plan.required_snapshot_date)}.`
  }
  return `Последние остатки — за ${formatDate(plan.snapshot_date)}. Для расчёта закупки нужна синхронизация за ${formatDate(plan.required_snapshot_date)}.`
}

export const deliveryDateLabel = (offer: PurchaseOfferDto) => formatDate(offer.delivery_date)

export const purchaseOfferToCartInput = (line: PurchaseLineDto, offer: PurchaseOfferDto) => {
  if (
    offer.catalog_item_id == null ||
    offer.product_type_id == null ||
    offer.private_supplier_id == null ||
    offer.unit == null ||
    offer.line_total == null ||
    offer.quantity == null ||
    offer.quantity <= 0
  ) {
    return null
  }
  const cartOffer: RestaurantCatalogOfferDto = {
    itemId: offer.catalog_item_id,
    supplierOrganizationId: null,
    supplierOrganizationName: offer.supplier_name,
    privateSupplierId: offer.private_supplier_id,
    legalName: offer.supplier_name,
    ogrn: null,
    inn: null,
    isRegisteredSupplier: false,
    itemName: offer.item_name,
    brand: null,
    country: null,
    unit: offer.unit,
    price: String(offer.price),
    minShipmentQty: null,
    packageSize: offer.package_size,
    productTypeId: offer.product_type_id,
    productTypeCode: null,
    productTypeName: line.product_name,
    productTypeActive: true,
    supplierResponseScore: null,
    rankingPrice: null,
    matchedTags: [],
  }
  return { offer: cartOffer, productTypeName: line.product_name, quantity: offer.quantity }
}

const roleLabel = (role: string) => {
  if (role === 'KITCHEN') {
    return 'Кухня'
  }
  if (role === 'BAR') {
    return 'Бар'
  }
  return 'Склад'
}

const linesToBuy = (lines: PurchaseLineDto[]) =>
  lines.filter((line) => (line.order_quantity ?? 0) > 0)

const linesEnough = (lines: PurchaseLineDto[]) =>
  lines.filter((line) => line.order_quantity === 0 && !line.insufficient_history)

const linesUnknown = (lines: PurchaseLineDto[]) => lines.filter((line) => line.insufficient_history)

const defaultOfferIndex = (line: PurchaseLineDto) => {
  const recommended = line.offers.findIndex((offer) => offer.recommended)
  return recommended >= 0 ? recommended : 0
}

const offerPrice = (offer: PurchaseOfferDto) =>
  `${formatMoney(offer.price)}${offer.unit ? `/${offer.unit}` : ''}`

const offerMeta = (offer: PurchaseOfferDto) =>
  [
    offerPrice(offer),
    `привезут ${deliveryDateLabel(offer)}`,
    offer.package_size ? `фасовка ${offer.package_size}` : null,
  ]
    .filter(Boolean)
    .join(' · ')

/**
 * Выбор поставщика в ячейке таблицы.
 *
 * Поле ввода здесь не к месту: вариантов два-три, печатать нечего, а рамка инпута
 * тянет ширину колонки и обрезает название. Поэтому меню: имя поставщика текстом.
 */
const OfferPicker = ({
  line,
  selectedIndex,
  onSelect,
}: {
  line: PurchaseLineDto
  selectedIndex: number
  onSelect: (index: number) => void
}) => {
  const [open, setOpen] = useState(false)
  const [menuBox, setMenuBox] = useState<{ top: number; left: number } | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLUListElement>(null)
  const offer = line.offers[selectedIndex] ?? null

  // Таблица прокручивается по горизонтали и обрезает всё, что вылезает за её край,
  // поэтому список живёт в body и позиционируется по триггеру.
  const placeMenu = () => {
    const box = rootRef.current?.getBoundingClientRect()
    if (!box) return
    setMenuBox({ top: box.bottom + window.scrollY + 4, left: box.left + window.scrollX })
  }

  const openMenu = () => {
    placeMenu()
    setOpen(true)
  }

  useEffect(() => {
    if (!open) return
    const closeOnOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return
      setOpen(false)
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    // Список не привязан к странице потоком — при прокрутке его двигаем за триггером.
    const reposition = () => placeMenu()
    document.addEventListener('mousedown', closeOnOutside)
    document.addEventListener('keydown', closeOnEscape)
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
    return () => {
      document.removeEventListener('mousedown', closeOnOutside)
      document.removeEventListener('keydown', closeOnEscape)
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [open])

  if (offer == null) {
    return null
  }

  return (
    <div className="market-offer-pick" ref={rootRef}>
      <button
        type="button"
        className="market-offer-pick__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Поставщик для «${line.product_name}»`}
        onClick={() => (open ? setOpen(false) : openMenu())}
      >
        <span className="market-offer-pick__name">{offer.supplier_name}</span>
        <IconChevronDown className="market-offer-pick__chevron" />
      </button>
      {open && menuBox
        ? createPortal(
            <ul
              ref={menuRef}
              className="market-offer-pick__list"
              role="listbox"
              style={{ top: menuBox.top, left: menuBox.left }}
            >
              {line.offers.map((item, index) => (
                <li key={`${item.catalog_item_id ?? 'offer'}-${item.supplier_name}-${item.price}`}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={index === selectedIndex}
                    className={
                      index === selectedIndex
                        ? 'market-offer-pick__option market-offer-pick__option--active'
                        : 'market-offer-pick__option'
                    }
                    onClick={() => {
                      onSelect(index)
                      setOpen(false)
                    }}
                  >
                    <span className="market-offer-pick__name">{item.supplier_name}</span>
                    <span className="market-muted">{offerMeta(item)}</span>
                  </button>
                </li>
              ))}
            </ul>,
            document.body,
          )
        : null}
    </div>
  )
}

const ToBuyRow = ({ line }: { line: PurchaseLineDto }) => {
  const { addOffer } = useCart()
  const [offerIndex, setOfferIndex] = useState(() => defaultOfferIndex(line))
  const [added, setAdded] = useState(false)
  const offer = line.offers[offerIndex] ?? null
  const cartInput = offer ? purchaseOfferToCartInput(line, offer) : null
  const deliveryLabel = offer == null ? null : deliveryDateLabel(offer)
  // Объём зависит от срока поставки: кто привезёт позже, у того заказываем больше.
  const orderQuantity = offer?.order_quantity ?? line.order_quantity
  // Фасовку показываем, только когда покупать придётся не в единицах учёта.
  const packageQuantity =
    offer?.quantity != null && offer.unit != null && offer.unit !== line.unit
      ? formatQty(offer.quantity, offer.unit)
      : null
  return (
    <tr>
      <td>
        <strong>{line.product_name}</strong>
        {line.usage_shifted ? (
          <span className="echat-badge echat-badge--warning">Расход сменился</span>
        ) : null}
      </td>
      <td>{roleLabel(line.store_role)}</td>
      <td>{formatQty(line.on_hand, line.unit)}</td>
      <td>{line.daily_usage == null ? '—' : formatQty(line.daily_usage, line.unit)}</td>
      <td>
        <strong>{orderQuantity == null ? '—' : formatQty(orderQuantity, line.unit)}</strong>
        {packageQuantity ? <div className="market-muted">{packageQuantity}</div> : null}
      </td>
      <td>
        {offer == null ? (
          <span className="market-muted">Нет цены в моих прайсах</span>
        ) : line.offers.length === 1 ? (
          <span className="market-offer-pick__name">{offer.supplier_name}</span>
        ) : (
          <OfferPicker
            line={line}
            selectedIndex={offerIndex}
            onSelect={(next) => {
              setOfferIndex(next)
              setAdded(false)
            }}
          />
        )}
      </td>
      <td>{deliveryLabel ?? '—'}</td>
      <td>{offer?.line_total == null ? '—' : <strong>{formatMoney(offer.line_total)}</strong>}</td>
      <td>
        {cartInput ? (
          <button
            type="button"
            className="echat-btn echat-btn--ghost"
            onClick={() => {
              addOffer(cartInput)
              setAdded(true)
            }}
          >
            {added ? 'Добавлено' : 'В заявку'}
          </button>
        ) : (
          '—'
        )}
      </td>
    </tr>
  )
}

const ToBuyTable = ({ lines }: { lines: PurchaseLineDto[] }) => (
  <div className="market-table-wrap">
    <table className="market-table market-table--purchase">
      <thead>
        <tr>
          <th>Позиция</th>
          <th>Склад</th>
          <th>Остаток</th>
          <th>Медиана расхода в день</th>
          <th>К заказу</th>
          <th>Поставщик</th>
          <th>Доставка</th>
          <th>Сумма</th>
          <th>Действие</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((line) => (
          <ToBuyRow key={`${line.store_external_id}-${line.product_external_id}`} line={line} />
        ))}
      </tbody>
    </table>
  </div>
)

const StockTable = ({ lines }: { lines: PurchaseLineDto[] }) => (
  <div className="market-table-wrap">
    <table className="market-table">
      <thead>
        <tr>
          <th>Позиция</th>
          <th>Склад</th>
          <th>Остаток</th>
          <th>Медиана расхода в день</th>
          <th>К заказу</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((line) => (
          <tr key={`${line.store_external_id}-${line.product_external_id}`}>
            <td>
              <strong>{line.product_name}</strong>
              {line.usage_shifted ? (
                <span className="echat-badge echat-badge--warning">Расход сменился</span>
              ) : null}
            </td>
            <td>{roleLabel(line.store_role)}</td>
            <td>{formatQty(line.on_hand, line.unit)}</td>
            <td>{line.daily_usage == null ? '—' : formatQty(line.daily_usage, line.unit)}</td>
            <td>{line.order_quantity == null ? '—' : formatQty(line.order_quantity, line.unit)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

export const RestaurantPurchaseScreen = () => {
  const { session } = useAuth()
  const { activeVenueId, activeVenueName } = useActiveVenue()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [plan, setPlan] = useState<PurchasePlanDto | null>(null)
  const [connected, setConnected] = useState<boolean | null>(null)
  // Неделя по умолчанию: заказ идёт по свежему спросу. null отдаёт выбор окна серверу.
  const [usageWindowDays, setUsageWindowDays] = useState<number | null>(
    isDemoStandEnabled() ? null : 7,
  )

  useEffect(() => {
    if (!session || session.organizationId == null || activeVenueId == null) {
      setConnected(null)
      return
    }
    let cancelled = false
    setConnected(null)
    void loadInventoryConnection(session.organizationId, activeVenueId)
      .then(() => {
        if (!cancelled) setConnected(true)
      })
      .catch((connectionError) => {
        if (!cancelled)
          setConnected(
            connectionError instanceof ApiError && connectionError.status === 404 ? false : null,
          )
      })
    return () => {
      cancelled = true
    }
  }, [activeVenueId, session])

  useEffect(() => {
    if (!session || session.organizationId == null || activeVenueId == null) {
      setLoading(false)
      setPlan(null)
      return
    }
    const organizationId = session.organizationId
    const venueId = activeVenueId
    let cancelled = false
    void (async () => {
      setLoading(true)
      setError(null)
      try {
        const loaded = await loadPurchasePlan(organizationId, venueId, usageWindowDays)
        if (!cancelled) {
          setPlan(loaded)
        }
      } catch (loadError) {
        if (!cancelled) {
          setPlan(null)
          setError(formatApiErrorMessage(loadError, 'Не удалось собрать закупку'))
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [activeVenueId, session, usageWindowDays])

  const toBuy = plan ? linesToBuy(plan.lines) : []
  const enough = plan ? linesEnough(plan.lines) : []
  const unknown = plan ? linesUnknown(plan.lines) : []
  const staleMessage = plan ? stalePurchaseMessage(plan) : null

  return (
    <section className="market-page">
      <MarketPageHeader
        title="Заказ по остаткам"
        description={
          plan && !plan.stale
            ? `Заказ на ${formatDate(plan.order_date)}. Считаем так, чтобы после прихода на складе осталось ещё на ${plan.stock_days} дн.`
            : activeVenueName
              ? `Заведение «${activeVenueName}». Сколько заказать, чтобы хватило до прихода и ещё на несколько дней после.`
              : 'Сколько заказать, чтобы хватило до прихода и ещё на несколько дней после.'
        }
      />

      {isDemoStandEnabled() ? <InventoryDemoNotice /> : null}
      {activeVenueId == null ? <p className="market-muted">Выберите заведение в шапке.</p> : null}
      {activeVenueId != null && connected === false ? (
        <div className="market-empty-state">
          <h2>Подключите iiko, чтобы считать заказ по остаткам</h2>
          <p className="market-muted">
            Мы возьмём остатки и расход со складов, посчитаем, сколько заказать, и подберём лучшую
            цену из ваших прайсов.
          </p>
          <ol className="market-muted">
            <li>Укажите адрес сервера, логин и пароль API iiko.</li>
            <li>Назначьте складам роли: кухня или бар.</li>
            <li>Запустите первую синхронизацию.</li>
          </ol>
          <Link
            className="echat-btn echat-btn--primary"
            to={isDemoStandEnabled() ? '/demo/iiko' : '/settings/integrations'}
          >
            Подключить iiko
          </Link>
        </div>
      ) : null}
      {connected === false ? null : loading ? (
        <p className="echat-muted">Собираем закупку…</p>
      ) : null}
      {connected !== false && error ? (
        <p className="echat-error" role="alert">
          {error}
        </p>
      ) : null}

      {connected !== false && !loading && !error && staleMessage ? (
        <p className="echat-error" role="alert">
          {staleMessage}{' '}
          <Link to={isDemoStandEnabled() ? '/demo/iiko' : '/settings/integrations'}>
            Синхронизировать
          </Link>
        </p>
      ) : null}

      {connected !== false && !loading && !error && plan && !plan.stale ? (
        <>
          <div className="market-purchase-window">
            <SelectCombobox
              className="market-purchase-window__field"
              label="Расход считаем за"
              value={usageWindowDays == null ? '' : String(usageWindowDays)}
              options={[
                { value: '', label: 'Автоматически' },
                ...USAGE_WINDOW_DAYS.map((days) => ({
                  value: String(days),
                  label: dayCountLabel(days),
                })),
              ]}
              onChange={(next) => setUsageWindowDays(next ? Number(next) : null)}
            />
            <p className="market-muted market-purchase-window__hint">
              Считаем медиану суточного расхода, а не среднее: разовое большое списание не должно
              задирать заказ. «Автоматически» берёт самый длинный период с данными и сам
              переключается на неделю, когда расход изменился больше чем в полтора раза. Если за
              выбранный период меньше 7 дней с расходом, позиция уходит в «Мало данных о расходе».
            </p>
          </div>

          <h2>Нужно заказать</h2>
          {toBuy.length === 0 ? (
            <p className="market-muted">К этой поставке закупать нечего: остатка хватает.</p>
          ) : (
            <ToBuyTable lines={toBuy} />
          )}

          {enough.length > 0 ? (
            <>
              <h2>Хватает</h2>
              <StockTable lines={enough} />
            </>
          ) : null}

          {unknown.length > 0 ? (
            <>
              <h2>Мало данных о расходе</h2>
              <StockTable lines={unknown} />
            </>
          ) : null}
        </>
      ) : null}
    </section>
  )
}
